/**
 * @jsxRuntime classic
 * @jsx jsx
 *
 * Selected-agent hat rendered inside the Rovo Chat prompt input wrapper.
 */
import { lazy, Suspense } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery } from 'react-relay';

import AvatarSkeleton from '@atlaskit/avatar/Skeleton';
import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { RovoIcon } from '@atlaskit/logo';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { deriveAgentIdentity } from '@atlaskit/rovo-agent-components/ui/agent-identity/derive-agent-identity';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { isDefaultRovoChat } from '@atlassian/conversation-assistant-utils/default-agent';

import { type AgentInputHatIdentityAccountQuery } from './__generated__/AgentInputHatIdentityAccountQuery.graphql';
import messages from './agent-input-hat-messages';

const AgentProfileCardWrapper = lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/conversation-assistant-chat-prompt-input/agent-profile-card-wrapper" */
		'./AgentProfileCardWrapper'
	).then((mod) => ({ default: mod.AgentProfileCardWrapper })),
);

const styles = cssMap({
	container: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
	},
	agentTriggerBase: {
		display: 'inline-flex',
		alignItems: 'center',
		paddingBlock: token('space.050'),
		paddingInline: token('space.100'),
		borderRadius: token('radius.medium'),
	},
	agentTriggerInteractive: {
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
		'&:active': {
			backgroundColor: token('elevation.surface.pressed'),
		},
	},
});

export type AgentInputHatProps = {
	agentName?: string;
	agentId?: string;
	agentNamedId?: string;
	imageUrl?: string;
	agentIdentityAccountId?: string;
	creatorType?: string;
	isForgeAgent?: boolean;
	forgeAgentIconUrl?: string;
	cloudId?: string;
	onDismiss?: () => void;
	testId?: string;
};

const getAccountIdFromIdentityAccountId = (value: string | undefined): string | undefined => {
	const trimmedValue = value?.trim();
	if (!trimmedValue) {
		return undefined;
	}

	const identityUserAriPrefix = 'ari:cloud:identity::user/';
	if (trimmedValue.startsWith(identityUserAriPrefix)) {
		return trimmedValue.slice(identityUserAriPrefix.length);
	}

	return trimmedValue.startsWith('ari:') ? undefined : trimmedValue;
};

const AgentInputHatIdentityAccount = ({
	identityAccountId,
	fallback,
}: {
	identityAccountId: string;
	fallback: JSX.Element;
}): JSX.Element => {
	const data = useLazyLoadQuery<AgentInputHatIdentityAccountQuery>(
		graphql`
			query AgentInputHatIdentityAccountQuery($identityAccountId: ID!) {
				user(accountId: $identityAccountId) {
					name
					picture
				}
			}
		`,
		{ identityAccountId },
		{ fetchPolicy: 'store-or-network' },
	);
	const name = data.user?.name?.trim();
	const picture = data.user?.picture?.trim();

	if (!name || !picture) {
		return fallback;
	}

	return (
		<Inline alignBlock="center" space="space.075">
			<AgentAvatar imageUrl={picture} size="small" name={name} label={name} showBorder={false} />
			<Text size="medium" color="color.text" weight="regular">
				{name}
			</Text>
		</Inline>
	);
};

export const AgentInputHat = ({
	agentName,
	agentId,
	agentNamedId,
	agentIdentityAccountId,
	imageUrl,
	creatorType,
	isForgeAgent,
	forgeAgentIconUrl,
	cloudId,
	onDismiss,
	testId,
}: AgentInputHatProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const isDefaultRovo = isDefaultRovoChat({ agentId, agentNamedId });
	const identityAccountId = isDefaultRovo
		? undefined
		: getAccountIdFromIdentityAccountId(agentIdentityAccountId);
	const hasSpecialistIdentity = Boolean(identityAccountId);
	const { visibleName } = hasSpecialistIdentity
		? { visibleName: undefined }
		: deriveAgentIdentity({
				agentId: isDefaultRovo ? undefined : agentId,
				agentName,
				agentNamedId: isDefaultRovo ? undefined : agentNamedId,
				agentIdentityAccountId,
				imageUrl,
				creatorType,
				isForgeAgent,
				forgeAgentIconUrl,
				defaultName: formatMessage(messages.defaultAgentName),
			});
	const hasProfileCard = Boolean(identityAccountId && cloudId);
	const fallback = hasSpecialistIdentity ? (
		<Inline alignBlock="center" space="space.075">
			<AvatarSkeleton appearance="circle" size="small" />
			<Skeleton width="80px" height="12px" />
		</Inline>
	) : (
		<Inline alignBlock="center" space="space.075">
			<RovoIcon label="" shouldUseHexLogo size="xsmall" />
			{visibleName ? (
				<Text size="medium" color="color.text" weight="regular">
					{visibleName}
				</Text>
			) : null}
		</Inline>
	);

	const agentTriggerBox = (
		<Box
			xcss={cx(
				styles.agentTriggerBase,
				hasProfileCard ? styles.agentTriggerInteractive : undefined,
			)}
		>
			{identityAccountId ? (
				<ErrorBoundary fallback={fallback}>
					<Suspense fallback={fallback}>
						<AgentInputHatIdentityAccount
							identityAccountId={identityAccountId}
							fallback={fallback}
						/>
					</Suspense>
				</ErrorBoundary>
			) : (
				fallback
			)}
		</Box>
	);

	const wrappedAgentTriggerBox = hasProfileCard ? (
		<Suspense fallback={agentTriggerBox}>
			<AgentProfileCardWrapper identityAccountId={identityAccountId!} cloudId={cloudId!}>
				{agentTriggerBox}
			</AgentProfileCardWrapper>
		</Suspense>
	) : (
		agentTriggerBox
	);

	return (
		<Box xcss={styles.container} testId={testId}>
			<Inline alignBlock="center" spread="space-between" space="space.075">
				{wrappedAgentTriggerBox}
				{typeof onDismiss === 'function' ? (
					<IconButton
						appearance="subtle"
						spacing="compact"
						icon={CrossIcon}
						label={formatMessage(messages.closeLabel)}
						onClick={onDismiss}
					/>
				) : null}
			</Inline>
		</Box>
	);
};
